/**
 * FinalCutsPanel (成片 tab) — gallery of EXPORTED final cuts.
 *
 * [REFACTOR-2026-06-09] Each export is an INDEPENDENT, kept cut (versioned by
 * export_seq) — a render can have many cuts. This tab lists every cut (newest
 * first), grouped by render, so re-exporting a polished render accumulates
 * versions instead of overwriting. Plays inline via the JWT-authed per-cut
 * endpoint; the Renders/精修 tab remains the editing workbench.
 */
import { Download, Film, Loader2, Trash2 } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { dramaApi, dramaQueryKeys } from '@/lib/api/drama'
import type { DramaEditPlan, DramaFinalCut, DramaLang } from '@/types/drama'
import { AuthedVideo } from '@/components/drama/AuthedVideo'
import { dramaT } from '@/components/drama/i18n'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'

interface Props {
  projectId: string
  finalCuts: DramaFinalCut[]
  editPlans: DramaEditPlan[]
  lang: DramaLang
}

export function FinalCutsPanel({ projectId, finalCuts, editPlans, lang }: Props) {
  const t = dramaT(lang)
  const queryClient = useQueryClient()
  const cuts = finalCuts || []

  const removeMutation = useMutation({
    mutationFn: (cutId: string) => dramaApi.removeFinalCut(projectId, cutId),
    onSuccess: () => {
      toast({ title: t('cuts.removed') })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
    },
    onError: (e: any) =>
      toast({
        title: t('toast.deleteFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  if (cuts.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed rounded-lg text-muted-foreground text-sm">
        <Film className="w-8 h-8 mx-auto mb-2 text-muted-foreground/60" />
        {t('cuts.empty')}
      </div>
    )
  }

  const planTitle = (planCode: string | null) =>
    (planCode && editPlans.find((p) => p.plan_code === planCode)?.title) || null

  const fmtDate = (iso: string | null) => {
    if (!iso) return ''
    const d = new Date(iso)
    return isNaN(d.getTime()) ? '' : d.toLocaleString()
  }

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
      {cuts.map((c) => (
        <div key={c.cut_id} className="border rounded-lg bg-card overflow-hidden flex flex-col">
          <AuthedVideo
            path={dramaApi.finalCutFileUrl(projectId, c.cut_id)}
            stream
            controls
            preload="metadata"
            className="w-full bg-black aspect-[9/16] max-h-[420px]"
          />
          <div className="p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {planTitle(c.plan_code) || c.render_code || c.name}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {c.render_code}
                  {c.export_seq != null && ` · #v${c.export_seq}`}
                  {c.duration_seconds != null && ` · ${c.duration_seconds.toFixed(1)}s`}
                </div>
                {c.created_at && (
                  <div className="text-[10px] text-muted-foreground/70">{fmtDate(c.created_at)}</div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button asChild variant="outline" size="sm">
                  <a
                    href={dramaApi.finalCutFileUrl(projectId, c.cut_id)}
                    download={`${c.render_code || 'cut'}-v${c.export_seq || 1}.mp4`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1" /> {t('cuts.download')}
                  </a>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  title={t('cuts.removeHint')}
                  onClick={() => {
                    if (confirm(t('cuts.removeConfirm'))) removeMutation.mutate(c.cut_id)
                  }}
                  disabled={removeMutation.isPending && removeMutation.variables === c.cut_id}
                >
                  {removeMutation.isPending && removeMutation.variables === c.cut_id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
            </div>
            {c.provenance?.length > 0 && (
              <details>
                <summary className="text-xs text-muted-foreground cursor-pointer">
                  {t('cuts.sources', c.provenance.length)}
                </summary>
                <div className="mt-2 space-y-1 text-xs">
                  {c.provenance.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-muted-foreground">
                      <code className="px-1 rounded bg-muted">{p.segment_code}</code>
                      <span>EP {p.episode_number}</span>
                      <span>
                        {p.source_start_seconds.toFixed(1)}–{p.source_end_seconds.toFixed(1)}s
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
